import React, { Fragment, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { KeyMomentCategory, CardColor } from './types';
import {
  canonicalKeyMomentMomentCode,
  getCategoryLabel,
  getHalfLabel,
  getMinutesLabel,
  getKeyMomentPrimaryLabel,
  isPenaltyMissMomentCode,
  shouldHideKeyMomentClock,
  truncateKeyMomentSubstitutionPlayerName,
} from './utils';

import KeyMomentWhistleIcon from './KeyMomentWhistleIcon';
import KeyMomentsTeamLogoImg from './KeyMomentsTeamLogoImg';

import './KeyMoments.scss';

/**
 * Compact cut tooltip line for progress-bar gaps (matches carousel copy rules).
 */
function getCompactCutEventLine(item, labels, formatMessage) {
  const categoryLabel = getCategoryLabel(item.category, item.cardColor, labels, item.moment);

  if (isPenaltyMissMomentCode(item.moment)) {
    const title = formatMessage({
      id: 'keyMoments.moment.PENALTY_MISSED',
      defaultMessage: 'Missed penalty',
    });
    return item.score ? `${title} · ${item.score}` : title;
  }

  if (shouldHideKeyMomentClock(item)) {
    return item.score ? `${categoryLabel} · ${item.score}` : categoryLabel;
  }

  if (item.category === KeyMomentCategory.GOAL && item.score) {
    return `${categoryLabel} · ${item.score}`;
  }
  if (item.category === KeyMomentCategory.CARD) {
    return categoryLabel;
  }
  if (item.category === KeyMomentCategory.MATCH_STATUS && item.moment) {
    const id = `keyMoments.moment.${canonicalKeyMomentMomentCode(item.moment)}`;
    return formatMessage({ id, defaultMessage: labels.matchStatus });
  }
  return categoryLabel;
}

function KeyMomentCutPreview({
  item,
  leftPercent,
  isUnavailable,
}) {
  const { formatMessage } = useIntl();

  const labels = useMemo(() => ({
    goal: formatMessage({ id: 'keyMoments.category.goal', defaultMessage: 'Goal' }),
    yellowCard: formatMessage({ id: 'keyMoments.category.yellowCard', defaultMessage: 'Yellow Card' }),
    redCard: formatMessage({ id: 'keyMoments.category.redCard', defaultMessage: 'Red Card' }),
    substitution: formatMessage({ id: 'keyMoments.category.substitution', defaultMessage: 'Substitution' }),
    matchStatus: formatMessage({ id: 'keyMoments.category.matchStatus', defaultMessage: 'Status' }),
    unavailable: formatMessage({ id: 'keyMoments.unavailable', defaultMessage: 'Indisponível' }),
  }), [formatMessage]);

  if (!item) return null;

  const halfLabel = getHalfLabel(item.half);
  const minutesLabel = getMinutesLabel(item.minutes, item.half);
  const categoryLabelSubst = getCategoryLabel(item.category, item.cardColor, labels, item.moment);
  const timeWithHalf = halfLabel && minutesLabel
    ? `${minutesLabel} | ${halfLabel}`
    : `${minutesLabel}`;
  const penaltyMiss = isPenaltyMissMomentCode(item.moment);
  const hideClock = shouldHideKeyMomentClock(item);
  const eventOnlyLogosSubst = !item.teamUrl && item.iconUrl;

  if (item.category === KeyMomentCategory.MATCH_STATUS) {
    const statusLabel = getKeyMomentPrimaryLabel(item, labels, formatMessage);
    const statusIcon = item.iconUrl ? (
      <img
        className="dtv-web-c-key-moment-cut-preview__status-icon-img"
        src={item.iconUrl}
        alt=""
      />
    ) : (
      <KeyMomentWhistleIcon className="dtv-web-c-key-moment-cut-preview__status-whistle" item={item} />
    );
    const statusRow = (
      <div className="dtv-web-c-key-moment-cut-preview__status-pill">
        {statusIcon}
        <span className="dtv-web-c-key-moment-cut-preview__status-label">{statusLabel}</span>
      </div>
    );

    let statusSurface = statusRow;
    if (isUnavailable) {
      statusSurface = (
        <div className="dtv-web-c-key-moment-cut-preview__surface">
          {statusRow}
        </div>
      );
    }

    return (
      <div
        className={classnames(
          'dtv-web-c-key-moment-cut-preview',
          'dtv-web-c-key-moment-cut-preview--match-status',
          isUnavailable && 'dtv-web-c-key-moment-cut-preview--unavailable',
        )}
        style={{ left: `${leftPercent}%` }}
        role="status"
      >
        {isUnavailable && (
          <span className="dtv-web-c-key-moment-cut-preview__badge">
            {labels.unavailable}
          </span>
        )}
        {statusSurface}
      </div>
    );
  }

  if (item.category === KeyMomentCategory.SUBSTITUTION) {
    const subSegments = item.mergedSegments?.length > 0
      ? item.mergedSegments
      : [{
        incidenceId: item.incidenceId,
        playerName: item.playerName,
        playerNameIn: item.playerNameIn,
      }];

    const substTeamSlot = !eventOnlyLogosSubst ? (
      <KeyMomentsTeamLogoImg
        teamUrl={item.teamUrl}
        className="dtv-web-c-key-moment-cut-preview__subst-team-img"
      />
    ) : null;

    const substitutionBody = (
      <>
        <div
          className={classnames(
            'dtv-web-c-key-moment-cut-preview__subst-head',
            eventOnlyLogosSubst && 'dtv-web-c-key-moment-cut-preview__subst-head--event-only',
          )}
        >
          <div className="dtv-web-c-key-moment-cut-preview__subst-logos">
            {substTeamSlot}
            {item.iconUrl && (
              <img
                className="dtv-web-c-key-moment-cut-preview__subst-type-icon"
                src={item.iconUrl}
                alt=""
              />
            )}
          </div>
          <div className="dtv-web-c-key-moment-cut-preview__subst-meta">
            <div className="dtv-web-c-key-moment-cut-preview__subst-time">{timeWithHalf}</div>
            <div className="dtv-web-c-key-moment-cut-preview__subst-category">
              {categoryLabelSubst}
            </div>
          </div>
        </div>

        {(subSegments.some((s) => s.playerName || s.playerNameIn)) && (
          <>
            <div className="dtv-web-c-key-moment-cut-preview__subst-divider" aria-hidden />
            {subSegments.map((seg, segIdx) => (
              <Fragment key={seg.incidenceId}>
                {segIdx > 0 ? (
                  <div
                    className="dtv-web-c-key-moment-cut-preview__subst-multi-gap"
                    aria-hidden="true"
                  />
                ) : null}
                <div className="dtv-web-c-key-moment-cut-preview__subst-grid">
                  <div className="dtv-web-c-key-moment-cut-preview__subst-cell dtv-web-c-key-moment-cut-preview__subst-cell--out">
                    <span
                      className={classnames(
                        'dtv-web-c-key-moment-cut-preview__subst-tri',
                        'dtv-web-c-key-moment-cut-preview__subst-tri--out',
                      )}
                      aria-hidden="true"
                    >
                      ▼
                    </span>
                    <span
                      className="dtv-web-c-key-moment-cut-preview__subst-name"
                      title={seg.playerName || undefined}
                    >
                      {seg.playerName
                        ? truncateKeyMomentSubstitutionPlayerName(seg.playerName)
                        : '–'}
                    </span>
                  </div>
                  <div className="dtv-web-c-key-moment-cut-preview__subst-cell dtv-web-c-key-moment-cut-preview__subst-cell--in">
                    <span
                      className={classnames(
                        'dtv-web-c-key-moment-cut-preview__subst-tri',
                        'dtv-web-c-key-moment-cut-preview__subst-tri--in',
                      )}
                      aria-hidden="true"
                    >
                      ▲
                    </span>
                    <span
                      className="dtv-web-c-key-moment-cut-preview__subst-name"
                      title={seg.playerNameIn || undefined}
                    >
                      {seg.playerNameIn
                        ? truncateKeyMomentSubstitutionPlayerName(seg.playerNameIn)
                        : '–'}
                    </span>
                  </div>
                </div>
              </Fragment>
            ))}
          </>
        )}
      </>
    );

    let substitutionSurface = substitutionBody;
    if (isUnavailable) {
      substitutionSurface = (
        <div className="dtv-web-c-key-moment-cut-preview__surface">
          {substitutionBody}
        </div>
      );
    }

    return (
      <div
        className={classnames(
          'dtv-web-c-key-moment-cut-preview',
          'dtv-web-c-key-moment-cut-preview--substitution',
          isUnavailable && 'dtv-web-c-key-moment-cut-preview--unavailable',
        )}
        style={{ left: `${leftPercent}%` }}
        role="status"
      >
        {isUnavailable && (
          <span className="dtv-web-c-key-moment-cut-preview__badge">
            {labels.unavailable}
          </span>
        )}
        {substitutionSurface}
      </div>
    );
  }

  const eventLine = getCompactCutEventLine(item, labels, formatMessage);
  const isMergedMultiCard = item.category === KeyMomentCategory.CARD
    && (item.mergedSegments?.length > 1);
  const iconOnlyCompact = !!item.iconUrl && !item.teamUrl;
  const wantsFallbackShield = !item.teamUrl && (
    item.category === KeyMomentCategory.GOAL
    || item.category === KeyMomentCategory.CARD
  );
  const hasMediaVisual = !!(item.teamUrl || item.iconUrl || wantsFallbackShield);

  let compactTeamSlot = null;
  if (item.teamUrl || wantsFallbackShield) {
    compactTeamSlot = (
      <KeyMomentsTeamLogoImg
        teamUrl={item.teamUrl}
        className="dtv-web-c-key-moment-cut-preview__media-team"
      />
    );
  }

  const compactRow = (
    <div className="dtv-web-c-key-moment-cut-preview__row">
      {hasMediaVisual && (
        <div
          className={classnames(
            'dtv-web-c-key-moment-cut-preview__media',
            iconOnlyCompact && 'dtv-web-c-key-moment-cut-preview__media--only-icon',
          )}
        >
          {compactTeamSlot}
          {item.iconUrl ? (
            <img
              className="dtv-web-c-key-moment-cut-preview__media-icon"
              src={item.iconUrl}
              alt=""
            />
          ) : null}
          {penaltyMiss && (item.teamUrl || item.iconUrl || wantsFallbackShield) && (
            <span className="dtv-web-c-key-moment-cut-preview__penalty-miss-mark" aria-hidden="true">
              ×
            </span>
          )}
        </div>
      )}
      <div
        className={classnames(
          'dtv-web-c-key-moment-cut-preview__text',
          hideClock && 'dtv-web-c-key-moment-cut-preview__text--no-clock',
        )}
      >
        {!hideClock ? (
          <div className="dtv-web-c-key-moment-cut-preview__time">{timeWithHalf}</div>
        ) : null}
        {isMergedMultiCard ? (
          <>
            <div
              className={classnames(
                'dtv-web-c-key-moment-cut-preview__event',
                hideClock && 'dtv-web-c-key-moment-cut-preview__event--solo-line',
                (item.cardColor === CardColor.RED && item.moment !== 'RED_CARD_SECOND_YELLOW')
                  && 'dtv-web-c-key-moment-cut-preview__event--card-red',
                (item.cardColor === CardColor.YELLOW || item.moment === 'RED_CARD_SECOND_YELLOW')
                  && 'dtv-web-c-key-moment-cut-preview__event--card-yellow',
              )}
            >
              {getCategoryLabel(KeyMomentCategory.CARD, item.cardColor, labels, item.moment)}
            </div>
            <div className="dtv-web-c-key-moment-cut-preview__merged-card-names">
              {item.mergedSegments
                .filter((seg) => seg.playerName)
                .map((seg) => (
                  <span
                    key={seg.incidenceId}
                    className="dtv-web-c-key-moment-cut-preview__merged-card-name"
                    title={seg.playerName}
                  >
                    {truncateKeyMomentSubstitutionPlayerName(seg.playerName)}
                  </span>
                ))}
            </div>
          </>
        ) : (
          <div
            className={classnames(
              'dtv-web-c-key-moment-cut-preview__event',
              hideClock && 'dtv-web-c-key-moment-cut-preview__event--solo-line',
              item.category === KeyMomentCategory.GOAL && 'dtv-web-c-key-moment-cut-preview__event--goal',
              (item.category === KeyMomentCategory.CARD && item.cardColor === CardColor.RED
                && item.moment !== 'RED_CARD_SECOND_YELLOW')
                && 'dtv-web-c-key-moment-cut-preview__event--card-red',
              (item.category === KeyMomentCategory.CARD && (
                item.cardColor === CardColor.YELLOW || item.moment === 'RED_CARD_SECOND_YELLOW'
              ))
                && 'dtv-web-c-key-moment-cut-preview__event--card-yellow',
              penaltyMiss && 'dtv-web-c-key-moment-cut-preview__event--penalty-missed',
            )}
          >
            {eventLine}
          </div>
        )}
      </div>
    </div>
  );

  let compactSurface = compactRow;
  if (isUnavailable) {
    compactSurface = (
      <div className="dtv-web-c-key-moment-cut-preview__surface">
        {compactRow}
      </div>
    );
  }

  return (
    <div
      className={classnames(
        'dtv-web-c-key-moment-cut-preview',
        isUnavailable && 'dtv-web-c-key-moment-cut-preview--unavailable',
      )}
      style={{ left: `${leftPercent}%` }}
      role="status"
    >
      {isUnavailable && (
        <span className="dtv-web-c-key-moment-cut-preview__badge">
          {labels.unavailable}
        </span>
      )}
      {compactSurface}
    </div>
  );
}

KeyMomentCutPreview.propTypes = {
  item: PropTypes.shape({
    incidenceId: PropTypes.number,
    moment: PropTypes.string,
    minutes: PropTypes.number,
    half: PropTypes.number,
    category: PropTypes.string,
    cardColor: PropTypes.oneOf(Object.values(CardColor)),
    teamUrl: PropTypes.string,
    iconUrl: PropTypes.string,
    score: PropTypes.string,
    playerName: PropTypes.string,
    playerNameIn: PropTypes.string,
    mergedSegments: PropTypes.arrayOf(PropTypes.shape({
      incidenceId: PropTypes.number,
      moment: PropTypes.string,
      cardColor: PropTypes.string,
      playerName: PropTypes.string,
      playerNameIn: PropTypes.string,
    })),
  }),
  leftPercent: PropTypes.number.isRequired,
  isUnavailable: PropTypes.bool,
};

KeyMomentCutPreview.defaultProps = {
  item: null,
  isUnavailable: false,
};

export default memo(KeyMomentCutPreview);
